import { useState } from "react";
import { Link } from "react-router-dom";
import { Scale, Trash2, Inbox } from "lucide-react";
import { toast } from "sonner";
import { ideasStore, useIdeas } from "@/lib/storage";
import { IdeaCard } from "@/components/IdeaCard";
import { CompareTable } from "@/components/CompareTable";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const MAX_COMPARE = 3;

const useSelection = () => {
  const [selected, setSelected] = useState([]);
  const toggle = (id) => {
    if (selected.includes(id)) return setSelected(selected.filter((s) => s !== id));
    if (selected.length >= MAX_COMPARE) return toast.error(`Puoi confrontare al massimo ${MAX_COMPARE} idee`);
    setSelected([...selected, id]);
  };
  const drop = (id) => setSelected(selected.filter((s) => s !== id));
  return { selected, toggle, drop };
};

const Toolbar = ({ onlyFav, setOnlyFav, count, onCompare }) => (
  <div className="flex gap-2">
    {[["Tutte", false], ["Preferite", true]].map(([l, v]) => (
      <button key={l} onClick={() => setOnlyFav(v)} data-testid={`filter-${v ? "favorites" : "all"}`}
        className={`rounded-full px-4 py-2 text-sm border transition-colors duration-200 ${onlyFav === v ? "bg-white/10 border-white/20 text-slate-100" : "border-white/10 text-slate-400 hover:text-slate-100"}`}>
        {l}
      </button>
    ))}
    <button onClick={onCompare} disabled={count < 2} data-testid="btn-compare-ideas"
      className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-5 py-2 text-sm font-semibold text-slate-950 transition-colors hover:bg-amber-400 disabled:opacity-40">
      <Scale className="w-4 h-4" /> Confronta ({count})
    </button>
  </div>
);

const EmptyState = ({ onlyFav }) => (
  <div className="mt-16 rounded-3xl border border-dashed border-white/10 p-14 text-center" data-testid="my-ideas-empty">
    <Inbox className="w-8 h-8 text-slate-600 mx-auto" />
    <p className="text-slate-400 mt-4">{onlyFav ? "Nessuna idea preferita." : "Non hai ancora salvato idee."}</p>
    <Link to="/genera" className="inline-block mt-6 rounded-full bg-amber-500 px-6 py-3 font-semibold text-slate-950 hover:bg-amber-400" data-testid="my-ideas-empty-cta">Genera le prime idee</Link>
  </div>
);

const SavedTile = ({ idea, index, checked, onToggle, onRemove }) => (
  <div className="flex flex-col">
    <div className="flex items-center justify-between mb-2 px-1">
      <label className="flex items-center gap-2 text-sm text-slate-400 cursor-pointer">
        <input type="checkbox" checked={checked} onChange={onToggle} className="accent-amber-500 w-4 h-4" data-testid={`saved-idea-${index + 1}-compare-checkbox`} />
        Confronta
      </label>
      <button onClick={onRemove} data-testid={`saved-idea-${index + 1}-remove-btn`} className="text-slate-500 hover:text-rose-400 transition-colors" title="Rimuovi">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
    <IdeaCard idea={idea} index={index} testId={`saved-idea-${index + 1}`} />
  </div>
);

export default function MyIdeas() {
  const all = useIdeas();
  const [onlyFav, setOnlyFav] = useState(false);
  const [open, setOpen] = useState(false);
  const { selected, toggle, drop } = useSelection();
  const saved = all.filter((i) => i.saved && (!onlyFav || i.favorite));
  const compared = all.filter((i) => selected.includes(i.id));

  const remove = (id) => { ideasStore.remove(id); drop(id); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14" data-testid="my-ideas-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="overline-label">Archivio</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-4">Le mie idee</h1>
          <p className="text-slate-400 mt-3">Salvate in questo browser. Seleziona fino a 3 idee per confrontarle.</p>
        </div>
        <Toolbar onlyFav={onlyFav} setOnlyFav={setOnlyFav} count={selected.length} onCompare={() => setOpen(true)} />
      </div>
      {saved.length === 0 ? <EmptyState onlyFav={onlyFav} /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
          {saved.map((idea, i) => (
            <SavedTile key={idea.id} idea={idea} index={i} checked={selected.includes(idea.id)} onToggle={() => toggle(idea.id)} onRemove={() => remove(idea.id)} />
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl bg-[#0d0f17] border-white/10" data-testid="compare-dialog">
          <DialogHeader><DialogTitle className="font-display">Confronto affiancato</DialogTitle></DialogHeader>
          <CompareTable ideas={compared} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
